import { createApp, computed } from "vue";
import { createPinia } from 'pinia';
import App from '@/App.vue';
import router from '@/router';
import i18n from "@/locales/i18n";
import { useLanguageStore } from '@/store/language';
import VueLazyload from 'vue3-lazyload';
import { useAuthStore } from './store/auth';
import helpers from "@/helpers";
import $ from 'jquery';
import 'jquery-nice-select';
import './assets/css/nice-select.css';

// Make jQuery globally accessible
window.$ = window.jQuery = $;

function initializeApp() {
  const app = createApp(App);

  // Note: Loading interceptors are handled in src/store/https.js
  // Do NOT add duplicate axios interceptors here as it causes race conditions
  // that can leave the loading state stuck, causing games to appear "hung"
  

  router.beforeEach((to, from, next) => {

    const authStore = useAuthStore()
    const isAuthenticated = authStore.getIsloggedIn
    if (to.matched.some(record => record.meta.requiresAuth)) {
      if (!isAuthenticated) {
        // User is not authenticated, redirect to login page
        next('/')
      }
    }

    let config = helpers.getConfig();
    let title = config? config.name: "Mars Gaming";

    // Default title if domain is not matched
    let defaultTitle = to.meta.title || "Mars Gaming";
    let defaultDescription = to.meta.description || "";

    // Set document title from domain
    document.title = (defaultTitle + (title ? " | " + title : ""));

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', defaultDescription);
    } else {
      const description = document.createElement('meta');
      description.name = 'description';
      description.content = defaultDescription;
      document.head.appendChild(description);
    }

    next();
  });
  

  app.use(createPinia())
  .use(router)
  .use(i18n)
  .use(VueLazyload, {
    preLoad: 2.0,              // Load images 2x viewport ahead (more aggressive preloading)
    error: require('@/assets/img/lazyload/error.webp'),
    loading: require('@/assets/img/lazyload/placeholder.webp'),
    attempt: 2,                // Retry failed loads once
    throttleWait: 50,          // Faster scroll event handling (default is 200ms)
    observer: true,            // Use IntersectionObserver for better performance
    observerOptions: {
      rootMargin: '500px',     // Start loading 500px before entering viewport
      threshold: 0.01          // Trigger when even 1% visible
    }
  })

  return app;
}

// Initialize the Vue application
const app = initializeApp();
const languageStore = useLanguageStore();
languageStore.initLocale();

// Track niceSelect initialization using WeakMap for proper garbage collection
const niceSelectInitialized = new WeakMap();

// Apply global jQuery logic
app.mixin({
  mounted() {
    // Apply Nice Select globally with deduplication
    this.$nextTick(() => {
      const selectElements = this.$el.querySelectorAll ? this.$el.querySelectorAll('select') : [];
      selectElements.forEach((select) => {
        if (!niceSelectInitialized.has(select)) {
          $(select).niceSelect();
          niceSelectInitialized.set(select, true);
        }
      });
    });
  },
  beforeUnmount() {
    // Clean up niceSelect instances to prevent memory leaks
    if (this.$el && this.$el.querySelectorAll) {
      const selectElements = this.$el.querySelectorAll('select');
      selectElements.forEach((select) => {
        if (niceSelectInitialized.has(select)) {
          $(select).niceSelect('destroy');
          niceSelectInitialized.delete(select);
        }
      });
    }
  },
});

app
  .provide(
    "isRtlLayout",
    computed(() => languageStore.isRtlLayout)
  )
  .mount('#app');
